// A per-DEVICE preference: cached in memory, persisted in the platform kv, shared live between every component
// that asks for the same key (useSyncExternalStore), never synced to another device. Home's "where I left off"
// (Lj, 2026-10-26: "wherever we leave off is where we should come back … doesn't need to sync across devices —
// mobile and web could be different departments"): the last department, the Home tab, each dashboard's range.
import { useSyncExternalStore, useCallback } from 'react';
import { platform } from '../platform';

const PREFIX = 'ops.device.';
const values = new Map();       // key → value (null = read, nothing stored)
const loading = new Map();      // key → Promise
const subs = new Map();         // key → Set(fn)

const kvGet = (k) => { try { return Promise.resolve(platform().kv.getItem(k)).catch(() => null); } catch { return Promise.resolve(null); } };
const kvSet = (k, v) => { try { Promise.resolve(platform().kv.setItem(k, String(v))).catch(() => {}); } catch { /* no kv */ } };
const emit = (key) => { for (const fn of subs.get(key) || []) fn(); };

function load(key) {
  if (values.has(key) || loading.has(key)) return;
  const p = kvGet(PREFIX + key).then((raw) => {
    if (!values.has(key)) values.set(key, raw == null || raw === '' ? null : String(raw));
    loading.delete(key); emit(key);
  });
  loading.set(key, p);
}

/** Read a device setting once (outside React). Resolves the stored string or null. */
export async function getDeviceSetting(key) {
  if (values.has(key)) return values.get(key);
  load(key); await loading.get(key); return values.get(key) ?? null;
}
export function setDeviceSetting(key, value) {
  const v = value == null ? null : String(value);
  values.set(key, v);
  if (v == null) kvSet(PREFIX + key, ''); else kvSet(PREFIX + key, v);
  emit(key);
}

/**
 * useDeviceSetting('home.range.field', 'week') → [value, set, ready]. `value` is the fallback until the kv answers
 * (`ready` false), then whatever this device last chose. `allowed` (optional) drops a stored value that no longer fits.
 */
export function useDeviceSetting(key, fallback = null, allowed = null) {
  load(key);
  const subscribe = useCallback((fn) => { if (!subs.has(key)) subs.set(key, new Set()); subs.get(key).add(fn); return () => subs.get(key)?.delete(fn); }, [key]);
  const get = useCallback(() => (values.has(key) ? values.get(key) : undefined), [key]);
  const raw = useSyncExternalStore(subscribe, get, get);
  const ready = raw !== undefined;
  const ok = raw != null && (!allowed || allowed.includes(raw));
  const set = useCallback((v) => setDeviceSetting(key, v), [key]);
  return [ok ? raw : fallback, set, ready];
}
